import React, { useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CButtonGroup,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import Empty from '../../shared/Empty'
import Pager from '../../shared/Pager'
import SkeletonList from '../../shared/SkeletonList'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'
import ScriptFormModal from './ScriptFormModal'
import ScriptCatalogConfigModal from './ScriptCatalogConfigModal'
import ScriptGlobalAvailabilityModal from './ScriptGlobalAvailabilityModal'

const perPage = 10

function ScriptsPage({ onError }) {
  const { toast } = useUI()
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [configScript, setConfigScript] = useState(null)
  const [globalScript, setGlobalScript] = useState(null)
  const [busyUuid, setBusyUuid] = useState('')

  async function load() {
    setLoading(true)
    const res = await request('scripts_manage')
    setLoading(false)
    if (!res.ok) {
      const detail = res.data?.error || 'No se pudieron cargar los scripts'
      if (onError) onError(detail)
      toast(detail, 'danger')
      return
    }
    setItems(Array.isArray(res.data) ? res.data : [])
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((script) => [script.name, script.url, script.description, script.keywords]
      .join(' ')
      .toLowerCase()
      .includes(term))
  }, [items, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage)

  function openNew() {
    setEditing(null)
    setModalOpen(true)
  }

  function openEdit(script) {
    setEditing(script)
    setModalOpen(true)
  }

  async function toggleStatus(script) {
    const next = script.status === 1 ? '0' : '1'
    setBusyUuid(script.uuid)
    const res = await request('script_update', { method: 'POST', body: { ...script, status: next } })
    setBusyUuid('')
    if (res.ok) {
      toast(`Script "${script.name}" ${next === '1' ? 'activado' : 'desactivado'}`, 'info')
      load()
    } else {
      toast(res.data?.error || 'No se pudo cambiar el estado del script', 'danger')
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Scripts</h1>
          <p>Gestiona el catalogo de scripts: nombre, URL, configuracion y estado.</p>
        </div>
        <CButtonGroup>
          <CButton color="light" onClick={load}><CIcon icon={icon('cilReload')} /> Recargar</CButton>
          <CButton color="success" className="btn-brand" onClick={openNew}><CIcon icon={icon('cilPlus')} /> Nuevo script</CButton>
        </CButtonGroup>
      </div>
      <CInputGroup className="search-wrap">
        <CInputGroupText><CIcon icon={icon('cilMagnifyingGlass')} /></CInputGroupText>
        <CFormInput placeholder="Buscar por nombre, URL o descripcion" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
      </CInputGroup>

      {loading ? <SkeletonList /> : null}

      {!loading && filtered.length === 0 ? (
        <Empty icon={icon('cilCode')} text="No hay scripts para mostrar." />
      ) : null}

      {!loading && filtered.length > 0 ? (
        <div className="table-responsive scripts-table-wrap">
          <CTable className="scripts-table" hover align="middle">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Nombre</CTableHeaderCell>
                <CTableHeaderCell>URL</CTableHeaderCell>
                <CTableHeaderCell>Estado</CTableHeaderCell>
                <CTableHeaderCell>Actualizado</CTableHeaderCell>
                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {pageItems.map((script) => (
                <CTableRow key={script.uuid}>
                  <CTableDataCell>
                    <strong>{script.name || 'Sin nombre'}</strong>
                    {script.description ? <div className="script-desc">{script.description}</div> : null}
                  </CTableDataCell>
                  <CTableDataCell>
                    <a className="script-url" href={script.url} target="_blank" rel="noreferrer">{script.url || '-'}</a>
                  </CTableDataCell>
                  <CTableDataCell>
                    <CBadge color={script.status === 1 ? 'success' : 'secondary'}>
                      {script.status === 1 ? 'Activo' : 'Inactivo'}
                    </CBadge>
                  </CTableDataCell>
                  <CTableDataCell>
                    <span className="script-date">{String(script.updated_date || script.created_date || '').slice(0, 10) || '-'}</span>
                  </CTableDataCell>
                  <CTableDataCell className="text-end">
                    <div className="scripts-actions">
                      <CButton color="light" size="sm" title="Disponibilidad global" aria-label={`Disponibilidad global de ${script.name}`} onClick={() => setGlobalScript(script)}>
                        <CIcon icon={icon('cilGlobeAlt')} />
                      </CButton>
                      <CButton color="light" size="sm" title="Editar datos" onClick={() => openEdit(script)}>
                        <CIcon icon={icon('cilPencil')} />
                      </CButton>
                      <CButton color="light" size="sm" title="Editar configuracion (JSON)" onClick={() => setConfigScript(script)}>
                        <CIcon icon={icon('cilSettings')} />
                      </CButton>
                      <CButton color="light" size="sm" title={script.status === 1 ? 'Desactivar' : 'Activar'} disabled={busyUuid === script.uuid} onClick={() => toggleStatus(script)}>
                        {busyUuid === script.uuid ? <CSpinner size="sm" /> : <CIcon icon={icon(script.status === 1 ? 'cilToggleOn' : 'cilToggleOff')} />}
                      </CButton>
                    </div>
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      ) : null}

      <Pager page={page} total={totalPages} onPage={setPage} />

      <ScriptFormModal
        open={modalOpen}
        script={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); load() }}
      />
      {globalScript ? <ScriptGlobalAvailabilityModal script={globalScript} onClose={() => setGlobalScript(null)} /> : null}
      {configScript ? (
        <ScriptCatalogConfigModal
          open={Boolean(configScript)}
          script={configScript}
          onClose={() => setConfigScript(null)}
          onSaved={load}
        />
      ) : null}
    </>
  )
}

export default ScriptsPage
